import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'

// updateUser({ password }) requires an active recovery session -- it does
// NOT establish one from the URL by itself. This page has to do that first:
// handle an implicit #access_token=...&type=recovery hash (setSession) or a
// PKCE ?code= (exchangeCodeForSession) explicitly, on top of listening for
// the PASSWORD_RECOVERY event the SDK fires when its own detectSessionInUrl
// handling succeeds -- belt and braces, since which path actually fires can
// vary. The "Update password" button stays disabled until one of these
// confirms a real recovery session; if none ever does, the link is shown as
// invalid/expired instead of surfacing "Auth session missing!" when the
// user tries to submit.
//
// Critical ordering bug fixed here: this used to call getSession() FIRST
// and short-circuit success if a session already existed, only falling
// back to parsing the URL if there was none. That's exactly backwards for
// the real-world case -- a logged-OUT visitor clicking a recovery link
// (every actual password reset) -- because it meant "already has a
// session" (i.e. already logged in) was the only reliably-working path,
// while a genuinely logged-out click depended on the hash/code still being
// in the URL by the time this ran, racing against the SDK's own automatic
// detectSessionInUrl handling of the exact same hash. The URL is now
// processed FIRST and unconditionally, before any getSession() check --
// getSession() is only ever consulted as the last-resort fallback, after
// there's no code/hash left to process.
export default function ResetPassword() {
  const navigate = useNavigate()

  const [checking, setChecking] = useState(true)
  const [recoveryReady, setRecoveryReady] = useState(false)
  const [linkInvalid, setLinkInvalid] = useState(false)

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let active = true

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (!active) return
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryReady(true)
        setLinkInvalid(false)
        setChecking(false)
      }
    })

    async function establishRecoverySession() {
      // Admin-generated links and standard recovery emails both land here
      // with #access_token=...&refresh_token=...&type=recovery in the hash
      // (implicit flow -- see supabaseClient.js). Check this FIRST, and
      // unconditionally: it works in any browser with no code_verifier
      // needed, which is exactly the case that matters (reset requested on
      // one device/browser, opened from wherever the link was sent).
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const accessToken = hashParams.get('access_token')
      const refreshToken = hashParams.get('refresh_token')
      const hashType = hashParams.get('type')

      if (accessToken && refreshToken && hashType === 'recovery') {
        const { data, error: setSessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        })
        if (!active) return
        if (setSessionError || !data.session) { setLinkInvalid(true); setChecking(false); return }
        setRecoveryReady(true)
        setChecking(false)
        return
      }

      // PKCE link. Only exchanges successfully on the same browser/device
      // that requested the reset -- failure here (including cross-device)
      // reads to the user as an invalid/expired link, which is accurate
      // from their perspective either way.
      const code = new URL(window.location.href).searchParams.get('code')
      if (code) {
        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)
        if (!active) return
        if (exchangeError || !data.session) { setLinkInvalid(true); setChecking(false); return }
        setRecoveryReady(true)
        setChecking(false)
        return
      }

      // No code, no recovery hash in this URL -- e.g. a refresh of this page
      // after the above already succeeded once, or the PASSWORD_RECOVERY
      // listener above is about to fire from the SDK's own detection. Give
      // that a brief window, then fall back to whatever session (if any)
      // is already active, before concluding the link is invalid/expired.
      setTimeout(async () => {
        if (!active) return
        const { data: { session } } = await supabase.auth.getSession()
        if (!active) return
        if (session) setRecoveryReady(true)
        else setLinkInvalid(true)
        setChecking(false)
      }, 1200)
    }

    establishRecoverySession()

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [])

  async function handleUpdate() {
    if (!recoveryReady) return
    if (!password || !confirm) { setError('Please fill in both fields.'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return }
    if (password !== confirm) { setError('Passwords do not match.'); return }
    setLoading(true)
    setError(null)
    const { error: updateError } = await supabase.auth.updateUser({ password })
    setLoading(false)
    if (updateError) { setError(updateError.message); return }
    // The event page (My Events) lives at "/" in this app, not a literal
    // "/my-events" path.
    navigate('/')
  }

  return (
    <div>
      <nav className="topnav">
        <a href="/" className="topnav-logo">PREDICTA</a>
      </nav>
      <div className="container-narrow">
        <div style={{ textAlign: 'center', paddingTop: '48px', marginBottom: '32px' }}>
          <h1 style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '36px', marginBottom: '8px' }}>New password</h1>
          <p style={{ color: 'var(--text-2)' }}>Choose a new password for your account</p>
        </div>
        <div className="card">
          {checking ? (
            <p className="text-muted" style={{ textAlign: 'center', padding: '16px 0' }}>Checking your reset link...</p>
          ) : linkInvalid ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '20px', marginBottom: '8px' }}>This reset link is invalid or has expired</h3>
              <p style={{ color: 'var(--text-2)', fontSize: '13px', marginBottom: '24px' }}>
                Reset links only work once and expire after a while. Request a new one to continue.
              </p>
              <a href="/forgot-password" className="btn btn-primary">Request a new link</a>
            </div>
          ) : (
            <>
              {error && (
                <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 'var(--r)', padding: '10px 14px', marginBottom: '16px' }}>
                  <p style={{ color: 'var(--red)', fontSize: '13px' }}>{error}</p>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: '7px' }}>New password</label>
                  <input type="password" placeholder="Min 6 characters" value={password} onChange={e => setPassword(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: '7px' }}>Confirm password</label>
                  <input type="password" placeholder="Repeat your password" value={confirm} onChange={e => setConfirm(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleUpdate()} />
                </div>
              </div>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={loading || !recoveryReady} style={{ width: '100%', padding: '12px' }}>
                {loading ? 'Updating...' : 'Update password'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
